
// import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useState, useEffect, useRef } from 'react';
import './styles.css'
import { Link, useMatch, useResolvedPath, useNavigate, useLocation } from "react-router-dom"
import { validateToken } from './functions/ValidateToken';
import { clearTokenSession } from './functions/authSession';
import { cognitoLogout } from './functions/cognitoAuth';
import config, { setRunnerUrl, clearRunnerUrl } from './Config';
import { authFetch } from './functions/authFetch';
import {
  switchLocalRunnerCluster,
  persistSelectedCluster,
  getStoredSelectedCluster,
  clearSelectedCluster,
  persistUserIdFromClusters,
  tierLabel,
} from './functions/clusterSwitch';
import homepic from './pages/images/home.png';

export default function Navbar(){
  const navigate = useNavigate();
  const location = useLocation();
  const [isValid, setIsValid] = useState(null);
  const [jobDropdownOpen, setJobDropdownOpen] = useState(false);
  const [deployDropdownOpen, setDeployDropdownOpen] = useState(false);
  const [imagesDropdownOpen, setImagesDropdownOpen] = useState(false);
  const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isClusterRoute = location.pathname.startsWith('/cluster');
  const [clusters, setClusters] = useState([]);
  const [selectedFull, setSelectedFull] = useState('');
  const [clusterBusy, setClusterBusy] = useState(false);
  const [clusterMenuOpen, setClusterMenuOpen] = useState(false);
  const [clusterLoading, setClusterLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const clusterPickerRef = useRef(null);
  const navRef = useRef(null);
  const isJobRoute = location.pathname === '/create-job' || location.pathname === '/log' || location.pathname === '/cron';
  const isConfigRoute = location.pathname === '/deployment/config' || location.pathname === '/deployment/secrets';
  const isDeployRoute = location.pathname.startsWith('/deployment') && !isConfigRoute;
  const isImagesRoute = location.pathname.startsWith('/cdci') || location.pathname.startsWith('/mcr');
  
  useEffect(() => {
    let cancelled = false;
    const checkToken = async () => {
      const valid = await validateToken();
      if (!cancelled) setIsValid(valid);
    };
    checkToken();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchClusters = async () => {
    setClusterLoading(true);
    try {
      const res = await authFetch(`${config.AUTH_API}/v1/get-user-info`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const list = await res.json();
      if (!Array.isArray(list)) return;
      setClusters(list);
      persistUserIdFromClusters(list);
      const stored = getStoredSelectedCluster();
      const chosen =
        (stored && list.some((x) => x.cluster_name === stored) ? stored : null) ||
        list[0]?.cluster_name;
      if (chosen) {
        const ent = list.find((x) => x.cluster_name === chosen);
        setSelectedFull(chosen);
        if (ent?.runner_url) {
          setRunnerUrl(ent.runner_url);
          persistSelectedCluster(chosen);
          fetchClusterTier(chosen, ent.runner_url, false);
        }
      }
    } catch (e) {
      console.error('get-user-info failed', e);
    } finally {
      setClusterLoading(false);
    }
  };

  useEffect(() => {
    if (!isValid) {
      setClusters([]);
      setSelectedFull('');
      return;
    }
    fetchClusters();
  }, [isValid]);

  const fetchClusterTier = async (clusterName, runnerUrl, cancelled) => {
    if (clusterName === 'default') {
      if (!cancelled) setSelectedTier('shared');
      return;
    }
    try {
      const res = await authFetch(`${runnerUrl}/v1/deployment/get-cluster-tier`, { method: 'GET' });
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (!cancelled) setSelectedTier(data.tier || null);
    } catch {
      setSelectedTier(null);
    }
  };

  useEffect(() => {
    if (!clusterMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (clusterPickerRef.current && !clusterPickerRef.current.contains(e.target)) {
        setClusterMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setClusterMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [clusterMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setJobDropdownOpen(false);
    setDeployDropdownOpen(false);
    setImagesDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [mobileMenuOpen]);

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setJobDropdownOpen(false);
    setDeployDropdownOpen(false);
    setImagesDropdownOpen(false);
    setConfigDropdownOpen(false);
  };

  const pickCluster = (next) => {
    if (!next || next === selectedFull) return;
    const entry = clusters.find((c) => c.cluster_name === next);
    if (!entry) return;
    switchLocalRunnerCluster(entry.cluster_name, entry.runner_url);
    window.location.reload();
  };

  const selectedClusterLabel =
    clusters.find((c) => c.cluster_name === selectedFull)?.trimmed_cluster_name ?? selectedFull;

  function LoginButton() {
    function login(){
      navigate('/login')
      }   

    return (
      <>
        <button className="login-button" onClick={login}>
          Log in
        </button>
      </>
    );
    };

  function AccountButton() {
    function account(){
      navigate('/account')
      }
    return (
      <>
        <button className="account-button" onClick={account}>
          My Account
        </button>
      </>
    );
  }   
  
  function LogoutButton() {
      const logout = () => {
        cognitoLogout();
        clearTokenSession();
        clearRunnerUrl();
        clearSelectedCluster();
        navigate('/');
        window.location.reload();
      };

    return (
      <>
        <button className="logout-button" onClick={logout}>
          Log out
        </button>
      </>
    );
    };
  
  

  function SignupButton() {
    function signup(){
      navigate('/signup')
      }   
    return (
      <>
        <button className="signup-button" onClick={signup}>
          Sign up
        </button>
      </>
    );
    };
  
    
  

    
  const authButtons = isValid ? (
    <>
      <AccountButton />
      <LogoutButton />
    </>
  ) : (
    <>
      <LoginButton />
      <SignupButton />
    </>
  );

  return <nav className="nav" ref={navRef}>
      <Link to="/" onClick={closeMenus}><img className="logo" src={homepic} alt="Mermadic" /></Link>
      <ul className={`nav-links${mobileMenuOpen ? ' nav-links--open' : ''}`}>
          <li className={isClusterRoute ? 'active' : ''}>
            <Link to="/cluster" onClick={closeMenus}>Cluster</Link>
          </li>
          <li
            className={`nav-dropdown ${isJobRoute ? 'active' : ''}`}
            onMouseEnter={() => setJobDropdownOpen(true)}
            onMouseLeave={() => setJobDropdownOpen(false)}
          >
            <span className="nav-dropdown-trigger">Workloads <span className="nav-chevron">&#9662;</span></span>
            <ul className={`nav-dropdown-menu${jobDropdownOpen ? ' open' : ''}`}>
              <li><Link to="/log" onClick={closeMenus}>Jobs</Link></li>
              <li><Link to="/cron" onClick={closeMenus}>Cron</Link></li>
            </ul>
          </li>
          <li
            className={`nav-dropdown ${isDeployRoute ? 'active' : ''}`}
            onMouseEnter={() => setDeployDropdownOpen(true)}
            onMouseLeave={() => setDeployDropdownOpen(false)}
          >
            <span className="nav-dropdown-trigger">Apps <span className="nav-chevron">&#9662;</span></span>
            <ul className={`nav-dropdown-menu${deployDropdownOpen ? ' open' : ''}`}>
              <li><Link to="/deployment/apps" onClick={closeMenus}>Deployments</Link></li>
              <li><Link to="/deployment/domains" onClick={closeMenus}>Domains</Link></li>
            </ul>
          </li>
          <li
            className={`nav-dropdown ${isConfigRoute ? 'active' : ''}`}
            onMouseEnter={() => setConfigDropdownOpen(true)}
            onMouseLeave={() => setConfigDropdownOpen(false)}
          >
            <span className="nav-dropdown-trigger">Config <span className="nav-chevron">&#9662;</span></span>
            <ul className={`nav-dropdown-menu${configDropdownOpen ? ' open' : ''}`}>
              <li><Link to="/deployment/config" onClick={closeMenus}>ConfigMaps</Link></li>
              <li><Link to="/deployment/secrets" onClick={closeMenus}>Secrets</Link></li>
            </ul>
          </li>
          <li
            className={`nav-dropdown ${isImagesRoute ? 'active' : ''}`}
            onMouseEnter={() => setImagesDropdownOpen(true)}
            onMouseLeave={() => setImagesDropdownOpen(false)}
          >
            <span className="nav-dropdown-trigger">Images <span className="nav-chevron">&#9662;</span></span>
            <ul className={`nav-dropdown-menu${imagesDropdownOpen ? ' open' : ''}`}>
              <li><Link to="/mcr/registry" onClick={closeMenus}>Registry</Link></li>
              <li><Link to="/mcr/push-history" onClick={closeMenus}>Build History</Link></li>
              <li><Link to="/cdci/automation" onClick={closeMenus}>Automations</Link></li>
            </ul>
          </li>
          <CustomLink to="documentation" onClick={closeMenus}>Documentation</CustomLink>
          <CustomLink to="pricing" onClick={closeMenus}>Pricing</CustomLink>
          {/* Only rendered in the collapsed menu — the bar hides its own auth buttons there. */}
          <li className="nav-menu-auth">{authButtons}</li>
      </ul>
      <div className="sign-in-container">
      {isValid ? (
                <>
                  {clusters.length >= 1 && selectedFull ? (
                    <div
                      ref={clusterPickerRef}
                      className={`nav-cluster-picker${clusterBusy ? ' nav-cluster-picker--busy' : ''}${clusterMenuOpen ? ' nav-cluster-picker--open' : ''}`}
                      title="Choose which cluster the app talks to"
                    >
                      <div className="nav-cluster-picker__row">
                        <div className="nav-cluster-picker__icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M4 14a4 4 0 0 1 4-4h8a3 3 0 1 1 0 6H8a4 4 0 0 1-4-2Z"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M8 10V8a4 4 0 0 1 7.5-2M12 6v4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                        <div className="nav-cluster-picker__field">
                          {/* Tier sits on the eyebrow line so the cluster name gets the full width. */}
                          <span className="nav-cluster-picker__eyebrow-row">
                            <span className="nav-cluster-picker__eyebrow" id="nav-cluster-eyebrow">
                              Cluster
                            </span>
                            {selectedTier && (
                              <span className={`nav-cluster-picker__tier nav-cluster-picker__tier--${selectedTier}`} aria-label={`Tier: ${tierLabel(selectedTier)}`}>
                                {tierLabel(selectedTier)}
                              </span>
                            )}
                          </span>
                          <div className="nav-cluster-picker__control">
                            <button
                              type="button"
                              id="nav-cluster-trigger"
                              className="nav-cluster-picker__trigger"
                              aria-haspopup="listbox"
                              aria-expanded={clusterMenuOpen}
                              aria-busy={clusterBusy}
                              aria-label={`Cluster: ${selectedClusterLabel}`}
                              disabled={clusterBusy}
                              onClick={() => {
                                const opening = !clusterMenuOpen;
                                setClusterMenuOpen(opening);
                                if (opening) fetchClusters();
                              }}
                            >
                              <span className="nav-cluster-picker__value" title={selectedClusterLabel}>{selectedClusterLabel}</span>
                              <span className="nav-cluster-picker__chevron" aria-hidden="true">
                                <svg viewBox="0 0 14 14" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3.5 5.25L7 9.5l3.5-4.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                      {clusterMenuOpen ? (
                        <ul className="nav-cluster-picker__menu" role="listbox" aria-labelledby="nav-cluster-eyebrow">
                          {clusterLoading && (
                            <li className="nav-cluster-picker__loading">Loading…</li>
                          )}
                          {clusters.map((c) => {
                            const isSelected = c.cluster_name === selectedFull;
                            return (
                              <li key={c.cluster_name} className="nav-cluster-picker__menu-item" role="none">
                                <button
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  className={`nav-cluster-picker__option${isSelected ? ' nav-cluster-picker__option--selected' : ''}`}
                                  onClick={() => {
                                    pickCluster(c.cluster_name);
                                    setClusterMenuOpen(false);
                                  }}
                                >
                                  <span className="nav-cluster-picker__option-label">{c.trimmed_cluster_name}</span>
                                  {isSelected ? (
                                    <span className="nav-cluster-picker__check" aria-hidden="true">
                                      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.5 8.5l2.8 2.8L12.5 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </span>
                                  ) : null}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="nav-bar-auth">{authButtons}</div>
                </>

            ) : (
                <div className="nav-bar-auth">{authButtons}</div>
            )}
      </div>
      <button
        type="button"
        className="nav-burger"
        aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={mobileMenuOpen}
        onClick={() => setMobileMenuOpen(o => !o)}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
          {mobileMenuOpen ? (
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          ) : (
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          )}
        </svg>
      </button>
  </nav>
  }

function CustomLink({ to, children, ...props }){
    const resolvedPath = useResolvedPath(to)
    const isActive = useMatch({ path: resolvedPath.pathname, end : true})
    return (
        <li className={isActive ? "active" : ""}>
                <Link to={ to }{...props}>{children}</Link>
        </li>
    )

}

   